import type { ReactNode } from 'react';

/**
 * Icons for the navigation panel, drawn inline.
 *
 * An icon library would be a second network request and a dependency for
 * fourteen glyphs; these are stroked paths on a 24-box, coloured by whatever
 * text colour the tile sets. Keys come from content/*.json, so a nav item names
 * its own icon and a missing or unknown key simply renders nothing rather than
 * breaking the panel.
 */
const PATHS: Record<string, ReactNode> = {
  doc: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" /></>),
  credit: (<><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M9 13h6" /></>),
  box: (<><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></>),
  users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>),
  grid: (<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  layout: (<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></>),
  gauge: (<><path d="M12 21a9 9 0 1 1 9-9" /><path d="M12 12l4-3" /></>),
  chart: (<><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></>),
  calc: (<><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M8 6h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15v4" /></>),
  shield: (<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>),
  swap: (<><path d="M17 2l4 4-4 4" /><path d="M3 6h18" /><path d="M7 22l-4-4 4-4" /><path d="M21 18H3" /></>),
  globe: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" /></>),
  card: (<><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></>),
  handshake: (<><path d="M11 17l-2 2a2 2 0 0 1-3-3l6-6 3 3" /><path d="M14 8l3-3 5 5-3 3" /><path d="M2 10l4-4" /></>),
};

export function NavIcon({ name, className }: { name?: string; className?: string }) {
  const paths = name ? PATHS[name] : undefined;
  if (!paths) {
    return null;
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}

/** Tints rotate so a column of items does not read as one block of colour. */
export const NAV_TINTS = [
  'bg-[rgba(244,115,14,0.10)] text-[#d9620a]',
  'bg-[rgba(31,75,238,0.09)] text-[#1f4bee]',
  'bg-[rgba(2,26,65,0.07)] text-[#33456b]',
] as const;
